import { Alert } from '@ui/themed';
import { t } from 'i18next';
import z from 'zod';
import { CredentialField } from '../../../../components/credential-field';
import type { Setting } from '../../../../context/settings-context';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { useAppForm } from '../../../../lib/app-form';
import { PARTNER_DOCS_URLS } from '../../constants';
import type { PartnerModalProps } from '../../types';
import { PartnerModal, saveAction } from '../partner-modal';
import { credentialValidator, usePartnerModalSave } from './shared';

const INTUITIVE = 'features:partners.components.intuitive_shipping' satisfies I18nPrefix;

export function IntuitiveShippingModal({ isOpen, onClose }: PartnerModalProps) {
  const settings = useSettings();
  const { save, isSaving } = usePartnerModalSave(onClose);

  const hasStoredKey = !!settings.integrationIntuitiveShippingApiKey;

  const form = useAppForm({
    defaultValues: { apiKey: '' },
    validators: { onSubmit: z.object({ apiKey: credentialValidator(hasStoredKey) }) },
    onSubmit: ({ value }) => {
      const payload: Setting[] = [{ key: 'integrationIntuitiveShipping', value: true }];
      const apiKey = value.apiKey.trim();
      if (apiKey) {
        payload.push({ key: 'integrationIntuitiveShippingApiKey', value: apiKey });
      }
      void save(payload);
    },
  });

  return (
    <PartnerModal
      isOpen={isOpen}
      onClose={onClose}
      title="Intuitive Shipping"
      description={t(`${INTUITIVE}.description`)}
      docsUrl={PARTNER_DOCS_URLS.intuitiveShipping}
      banner={
        !settings.useInternalShipping && (
          <Alert variant="warning" headingLevel="h2">
            {t(`${INTUITIVE}.enable_sparklayer_shipping_alert`)}
          </Alert>
        )
      }
      primaryAction={saveAction(() => void form.handleSubmit(), isSaving)}
    >
      <form.Field name="apiKey">
        {(field) => (
          <CredentialField
            label={t(`${INTUITIVE}.api_key_label`)}
            placeholder={t(`${INTUITIVE}.api_key_placeholder`)}
            tooltip={t(`${INTUITIVE}.api_key_tooltip`)}
            value={field.state.value}
            onChange={field.handleChange}
            hasStoredValue={hasStoredKey}
            error={field.state.meta.errors[0]?.message}
          />
        )}
      </form.Field>
    </PartnerModal>
  );
}
